import React from "react";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetDescription,
} from "@/components/ui/sheet";
import { Button } from "@/components/ui/button";
import { useSettings } from "@/contexts/SettingsContext";

const SWATCH = {
  blue: "#1d4ed8",
  emerald: "#057a55",
  amber: "#b45309",
  rose: "#be123c",
  violet: "#6d28d9",
};

const BG_PRESETS = [
  { value: "", label: "Default", color: "transparent" },
  { value: "#ffffff", label: "White", color: "#ffffff" },
  { value: "#f8fafc", label: "Slate 50", color: "#f8fafc" },
  { value: "#f1f5f9", label: "Slate 100", color: "#f1f5f9" },
  { value: "#eef2ff", label: "Indigo 50", color: "#eef2ff" },
  { value: "#ecfdf5", label: "Emerald 50", color: "#ecfdf5" },
  { value: "#fef3c7", label: "Amber 100", color: "#fef3c7" },
  { value: "#fee2e2", label: "Rose 100", color: "#fee2e2" },
  { value: "#fae8ff", label: "Fuchsia 100", color: "#fae8ff" },
  { value: "#0f172a", label: "Slate 900", color: "#0f172a" },
  { value: "#1e293b", label: "Slate 800", color: "#1e293b" },
  { value: "#18181b", label: "Zinc 900", color: "#18181b" },
];

export default function SettingsPanel({ open, onOpenChange }) {
  const { settings, update, reset } = useSettings();

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent
        side="right"
        className="w-[400px] sm:max-w-[420px] rounded-none border-l vsm-border vsm-surface overflow-y-auto"
        data-testid="settings-panel"
      >
        <SheetHeader>
          <div className="vsm-overline">Customize</div>
          <SheetTitle className="text-2xl font-bold tracking-tight">Workspace Settings</SheetTitle>
          <SheetDescription className="vsm-muted text-sm">
            Adjust theme, accent color, typography, layout density, and per-zone backgrounds.
          </SheetDescription>
        </SheetHeader>

        <div className="mt-8 space-y-8">
          <Section label="Theme">
            <div className="grid grid-cols-3 gap-2">
              {["light", "dark", "mono"].map((t) => (
                <OptionBtn
                  key={t}
                  active={settings.theme === t}
                  onClick={() => update({ theme: t })}
                  testId={`setting-theme-${t}`}
                >
                  {t}
                </OptionBtn>
              ))}
            </div>
          </Section>

          <Section label="Accent Color">
            <div className="grid grid-cols-5 gap-2">
              {Object.entries(SWATCH).map(([k, color]) => (
                <button
                  key={k}
                  data-testid={`setting-accent-${k}`}
                  onClick={() => update({ accent: k })}
                  className={`h-10 border vsm-border flex items-center justify-center ${
                    settings.accent === k ? "ring-2 ring-offset-2 ring-zinc-900" : ""
                  }`}
                  style={{ backgroundColor: color }}
                  title={k}
                />
              ))}
            </div>
          </Section>

          <Section label="Font Family">
            <div className="grid grid-cols-2 gap-2">
              {[
                { v: "inter", l: "Inter" },
                { v: "grotesk", l: "Space Grotesk" },
                { v: "mono", l: "JetBrains Mono" },
                { v: "serif", l: "Plex Serif" },
              ].map((f) => (
                <OptionBtn
                  key={f.v}
                  active={settings.font === f.v}
                  onClick={() => update({ font: f.v })}
                  testId={`setting-font-${f.v}`}
                >
                  {f.l}
                </OptionBtn>
              ))}
            </div>
          </Section>

          <Section label="Density">
            <div className="grid grid-cols-2 gap-2">
              {["comfortable", "compact"].map((d) => (
                <OptionBtn
                  key={d}
                  active={settings.density === d}
                  onClick={() => update({ density: d })}
                  testId={`setting-density-${d}`}
                >
                  {d}
                </OptionBtn>
              ))}
            </div>
          </Section>

          <BgPickerSection
            label="Menu Background"
            value={settings.sidebarBg}
            onChange={(v) => update({ sidebarBg: v })}
            testIdPrefix="setting-sidebar-bg"
          />

          <BgPickerSection
            label="Details Screen Background"
            value={settings.contentBg}
            onChange={(v) => update({ contentBg: v })}
            testIdPrefix="setting-content-bg"
          />

          <div className="pt-4 border-t vsm-border">
            <Button
              variant="outline"
              onClick={reset}
              data-testid="settings-reset"
              className="rounded-none w-full"
            >
              Reset to Defaults
            </Button>
          </div>
        </div>
      </SheetContent>
    </Sheet>
  );
}

function BgPickerSection({ label, value, onChange, testIdPrefix }) {
  return (
    <div data-testid={testIdPrefix}>
      <div className="vsm-overline mb-3">{label}</div>
      <div className="grid grid-cols-6 gap-2">
        {BG_PRESETS.map((p) => {
          const active = (value || "") === p.value;
          return (
            <button
              key={p.value || "default"}
              data-testid={`${testIdPrefix}-${p.value || "default"}`}
              onClick={() => onChange(p.value)}
              title={p.label}
              className={`h-9 border vsm-border relative ${
                active ? "ring-2 ring-offset-1 ring-zinc-900" : ""
              }`}
              style={{
                backgroundColor: p.color === "transparent" ? undefined : p.color,
                backgroundImage:
                  p.color === "transparent"
                    ? "linear-gradient(45deg, #e5e7eb 25%, transparent 25%, transparent 75%, #e5e7eb 75%), linear-gradient(45deg, #e5e7eb 25%, transparent 25%, transparent 75%, #e5e7eb 75%)"
                    : undefined,
                backgroundSize: p.color === "transparent" ? "8px 8px" : undefined,
                backgroundPosition: p.color === "transparent" ? "0 0, 4px 4px" : undefined,
              }}
            />
          );
        })}
      </div>
      <div className="mt-3 flex items-center gap-2">
        <span className="vsm-overline">Custom</span>
        <input
          type="color"
          data-testid={`${testIdPrefix}-custom`}
          value={value || "#ffffff"}
          onChange={(e) => onChange(e.target.value)}
          className="h-8 w-10 border vsm-border bg-transparent cursor-pointer"
        />
        <input
          type="text"
          data-testid={`${testIdPrefix}-hex`}
          placeholder="#hex"
          value={value || ""}
          onChange={(e) => onChange(e.target.value)}
          className="h-8 flex-1 border vsm-border vsm-surface px-2 text-sm font-mono rounded-none"
        />
        {value && (
          <button
            type="button"
            data-testid={`${testIdPrefix}-clear`}
            onClick={() => onChange("")}
            className="h-8 px-2 border vsm-border text-xs uppercase tracking-wider hover:bg-zinc-100 dark:hover:bg-zinc-800"
          >
            Clear
          </button>
        )}
      </div>
    </div>
  );
}

function Section({ label, children }) {
  return (
    <div>
      <div className="vsm-overline mb-3">{label}</div>
      {children}
    </div>
  );
}

function OptionBtn({ active, onClick, children, testId }) {
  return (
    <button
      data-testid={testId}
      onClick={onClick}
      className={`h-10 px-3 border vsm-border text-sm font-medium capitalize transition-colors ${
        active
          ? "vsm-primary-bg border-transparent"
          : "vsm-surface hover:bg-zinc-100 dark:hover:bg-zinc-800"
      }`}
    >
      {children}
    </button>
  );
}
